/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChevronDown16, ChevronRight16 } from '@carbon/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { MemberType } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import {
  CpBody00Placeholder,
  CpBody01Primary,
  CpBody0aPlaceholder,
} from 'components/atoms/customer-portal/typography';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import {
  Option,
  TeamOption,
  UserOption,
} from 'components/molecules/squad-picker/types';
import { OptionGroup } from 'components/organisms/field-editors/components/controlled-select-based-editor-popover';
import {
  AssigneePickerFilter,
  useAssigneeOptions,
} from 'hooks/use-assignee-options';
import { useTranslate } from 'hooks/use-translation';
import { CpInput } from './cp-input';
import { CpModal } from './modal/cp-modal';

interface CpTaskRespAssigneePickerProps {
  selectedOptions: Option[];
  onChange: (value: Option[]) => void;
  onClearAll?: () => void;
  projectId: number;
  visible?: boolean;
}

const isExtraPresent = (option: Option): option is UserOption | TeamOption => {
  return 'extra' in option && !!option?.extra;
};

export const ContentWrapper = styled.div`
  overflow-y: auto;
  width: 100%;
  height: calc(100svh - 220px);
`;

const StyledModal = styled(CpModal)`
  @media (max-width: 480px) {
    .ant-modal-content {
      height: calc(100% - 185px);
    }

    .ant-modal-body {
      padding: 0;
    }

    .ant-modal-footer {
      padding-top: var(--cp-spacing-05);
    }
  }
`;

const InputWrapper = styled.div`
  padding: var(--cp-spacing-05);
  ${CpInput} {
    border: 2px solid var(--cp-blue-60);
  }
`;

const SectionWrapper = styled.div`
  margin-bottom: var(--cp-spacing-05);

  &:first-child {
    margin-top: var(--cp-spacing-05);
  }
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  margin-bottom: var(--cp-spacing-05);
  padding: 0 var(--cp-spacing-04);
`;

const SectionTitle = styled(CpBody0aPlaceholder)`
  text-transform: uppercase;
  margin-bottom: 0;
`;

const SectionTitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    fill: var(--cp-text-placeholder);
  }
`;

const OptionContentWrapper = styled.div`
  display: flex;
  align-items: start;
  flex-direction: column;
  justify-content: start;
  width: calc(100% - 20px);
  margin-left: var(--cp-spacing-05);
  ${CpBody01Primary} {
    margin-bottom: var(--cp-spacing-02);
  }
`;

const OptionWrapper = styled.div<{
  isSelected?: boolean;
}>`
  padding: var(--cp-spacing-05) var(--cp-spacing-06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  width: 100%;
  border-bottom: 1px solid var(--cp-border-subtle);

  background: ${({ isSelected }) =>
    isSelected ? 'var(--cp-blue-20)' : 'var(--cp-white)'};

  &:active {
    background: var(--cp-blue-20);
  }

  &:first-child {
    margin-top: var(--cp-spacing-05);
  }

  ${(props) =>
    props?.isSelected
      ? css``
      : css`
          &:hover {
            background: var(--cp-blue-10);
          }
        `};
`;

const CpAssigneePickerUserOption: React.FC<{
  user: UserOption;
  onClick: (option: UserOption) => void;
  isSelected?: boolean;
}> = ({ user, onClick, isSelected }) => {
  return (
    <OptionWrapper
      isSelected={isSelected}
      onClick={() => {
        onClick(user);
      }}
    >
      <Avatar src={user.imageUrl} name={user?.label} />
      <OptionContentWrapper>
        <CpBody01Primary>{user?.label}</CpBody01Primary>
        <CpBody00Placeholder>{user?.description}</CpBody00Placeholder>
      </OptionContentWrapper>
    </OptionWrapper>
  );
};

const CpAssigneePickerTeamOption: React.FC<{
  team: TeamOption;
  onClick: (option: TeamOption) => void;
  isSelected?: boolean;
}> = ({ team, onClick, isSelected }) => {
  const extra = team?.extra as any;

  return (
    <OptionWrapper onClick={() => onClick(team)} isSelected={isSelected}>
      <PlaceholderAvatar
        isCustomerRole={
          extra?.teamType === MemberType.External ||
          extra?.teamType === MemberType.External
        }
        name={team?.label}
      />
      <OptionContentWrapper>
        <CpBody01Primary>{team?.label}</CpBody01Primary>
        <CpBody00Placeholder />
      </OptionContentWrapper>
    </OptionWrapper>
  );
};

const CpAssigneePickerSection: React.FC<{
  section: OptionGroup<string>;
  selectedIds: string[];
  onOptionClick: (option: Option) => void;
}> = ({ section, onOptionClick, selectedIds }) => {
  const optionsForThisSection = section.options;

  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <SectionWrapper key={section.label}>
      <SectionTitleWrapper onClick={toggleExpand}>
        <SectionTitleIcon>
          {isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
        </SectionTitleIcon>
        <SectionTitle>{section.label}</SectionTitle>
      </SectionTitleWrapper>
      {isExpanded ? (
        optionsForThisSection.map((_option) => {
          if (isExtraPresent(_option as any)) {
            const option = _option as Option;
            const { type } = option.extra as any;
            const isSelected = selectedIds.includes(option?.value);

            if (type === 'user') {
              return (
                <CpAssigneePickerUserOption
                  key={option.value}
                  user={option as UserOption}
                  onClick={(c) => onOptionClick(c as Option)}
                  isSelected={isSelected}
                />
              );
            }

            return (
              <CpAssigneePickerTeamOption
                key={option.value}
                team={option as TeamOption}
                onClick={onOptionClick}
                isSelected={isSelected}
              />
            );
          }

          return <></>;
        })
      ) : (
        <></>
      )}
    </SectionWrapper>
  );
};

export const CpTaskRespAssigneePicker: React.FC<CpTaskRespAssigneePickerProps> =
  ({ selectedOptions, onChange, projectId, visible }) => {
    const [searchText, setSearchText] = useState<string>('');
    const [dirty, setDirty] = useState(selectedOptions ?? []);
    const translate = useTranslate();

    const options = useAssigneeOptions({
      projectId: ['project_id', projectId],
      isPrivate: false,
      filter: AssigneePickerFilter.All,
      options: {},
    });

    const selectedIds = useMemo(() => {
      return dirty.map((option) => option?.value);
    }, [dirty]);

    const filteredOptions = useMemo(() => {
      if (!searchText) {
        return options?.map((section) => {
          if ((section as any).id === 'assignees') {
            return section;
          }

          return {
            ...section,
            options: section.options.filter(
              (option) => !selectedIds.includes(option.value),
            ),
          };
        });
      }

      return options.map((section) => {
        if ((section as any).id === 'assignees') {
          return {
            ...section,
            options: section.options.filter((option) =>
              option.label.toLowerCase().includes(searchText.toLowerCase()),
            ),
          };
        }

        return {
          ...section,
          options: section.options.filter(
            (option) =>
              option.label.toLowerCase().includes(searchText.toLowerCase()) &&
              !selectedIds.includes(option.value),
          ),
        };
      });
    }, [options, searchText, selectedIds]);

    const onSearchChange: React.ChangeEventHandler<HTMLInputElement> =
      useCallback((e) => {
        setSearchText(e.target.value);
      }, []);

    const onOptionClick = useCallback(
      (option: Option) => {
        const newSelectedOptions = dirty.includes(option)
          ? dirty.filter((o) => o !== option)
          : [...dirty, option];

        setDirty(newSelectedOptions);
      },
      [dirty],
    );

    const onOk = useCallback(() => {
      onChange(dirty);
    }, [dirty, onChange]);

    useEffect(() => {
      if (visible) {
        setDirty(selectedOptions);
      }
    }, [selectedOptions, visible]);

    return (
      <StyledModal
        onCancel={() => {
          onChange(selectedOptions);
        }}
        getContainer={document.getElementById('cp-modal-root') ?? undefined}
        visible={visible}
        title={<FormattedMessage id="assign_to" />}
        okText={<FormattedMessage id="done" />}
        onOk={onOk}
        cancelButtonProps={{
          hidden: true,
        }}
      >
        <InputWrapper>
          <CpInput
            placeholder={translate('search')}
            value={searchText}
            onChange={onSearchChange}
          />
        </InputWrapper>
        <ContentWrapper>
          {filteredOptions.map((section) => {
            return (
              <CpAssigneePickerSection
                section={{
                  ...section,
                  options:
                    (section as any)?.id === 'assignees'
                      ? dirty
                      : section?.options.filter(
                          (option) => !selectedIds.includes(option.value),
                        ),
                }}
                onOptionClick={onOptionClick}
                selectedIds={selectedIds}
              />
            );
          })}
        </ContentWrapper>
      </StyledModal>
    );
  };
